import type { FormEvent } from 'react';
import type { DialogProps } from '@mui/material/Dialog';

import { CloseIcon } from 'app/components/core/icons';
import InputPassword from 'app/components/flat/ui/Inputs/InputPassword';
import PasswordCriteriaChecklist from 'app/components/flat/ui/PasswordCriteriaChecklist';

import { FIRST_PASSWORD_RULES } from './rules';
import { FIRST_PASSWORD_MODAL_TEXTS as texts } from './texts';
import type { UseFirstPasswordModalInput } from './types';
import { useFirstPasswordModal } from './useFirstPasswordModal';
import * as S from './styles';

const TITLE_ID = 'first-password-modal-title';
const DESCRIPTION_ID = 'first-password-modal-description';

export const FirstPasswordModal = ({
  isAnotherModalOpen,
}: UseFirstPasswordModalInput) => {
  const modal = useFirstPasswordModal({ isAnotherModalOpen });

  const handleClose: NonNullable<DialogProps['onClose']> = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }

    modal.dismiss();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    modal.submit();
  };

  return (
    <S.Dialog
      open={modal.isOpen}
      onClose={handleClose}
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
    >
      <S.CloseButton
        type="button"
        aria-label={texts.close}
        onClick={modal.dismiss}
      >
        <CloseIcon size={16} />
      </S.CloseButton>
      <S.Form noValidate onSubmit={handleSubmit}>
        <S.Content>
          <S.Texts>
            <S.Title id={TITLE_ID}>{texts.title}</S.Title>
            <S.Description id={DESCRIPTION_ID}>
              {texts.description}
            </S.Description>
          </S.Texts>
          <input
            type="email"
            autoComplete="username"
            value={modal.userEmail}
            readOnly
            hidden
          />
          <S.Fields>
            <S.Field>
              <InputPassword
                label={texts.passwordLabel}
                value={modal.password}
                onChange={(event) => modal.changePassword(event.target.value)}
                errorMessage={modal.passwordError}
                disabled={modal.isSubmitting}
                showVisibilityToggle
                required
                autoComplete="new-password"
              />
              <PasswordCriteriaChecklist
                password={modal.password}
                rules={FIRST_PASSWORD_RULES}
                visible
              />
            </S.Field>
            <InputPassword
              label={texts.confirmationLabel}
              value={modal.confirmation}
              onChange={(event) => modal.changeConfirmation(event.target.value)}
              onBlur={modal.blurConfirmation}
              errorMessage={modal.confirmationError}
              disabled={modal.isSubmitting}
              showVisibilityToggle
              required
              autoComplete="new-password"
            />
          </S.Fields>
        </S.Content>
        <S.Actions>
          <S.SecondaryAction
            type="button"
            onClick={modal.dismiss}
            disabled={modal.isSubmitting}
          >
            {texts.later}
          </S.SecondaryAction>
          <S.PrimaryAction
            type="submit"
            disabled={!modal.canSubmit}
            aria-busy={modal.isSubmitting || undefined}
          >
            {texts.submit}
          </S.PrimaryAction>
        </S.Actions>
      </S.Form>
    </S.Dialog>
  );
};
